import {
  addTrackSegment,
  createTrackItem,
  insertAfterTrack,
  insertBeforeTrack,
  removeTrackSegment,
} from '@com/components/timeline-panel/utils/track-action'
import { IvSegmentType, IvTrackType } from '../enums'
import {
  _IvTrack,
  InferIvSegment,
  IvSegment,
  IvSegmentAudio,
  IvSegmentOther,
  IvTrack,
  IvTrackIndex,
  IvTrackSegmentMapType,
} from '../types'
import { isSegmentAudio, isSegmentIv, isSegmentVideo } from '.'
import { getConflictSegment } from '@com/components/timeline-panel/utils/track-insert-position'
import { typeMap } from '../configs'
import { patchSegment } from './segment'
import { trackHeightMap } from './track-height'
import { trackIconMap } from './track-icon'

export function getGroupIdByTrack<T extends IvTrackType>(
  track: _IvTrack<T> | IvTrack,
): IvSegment['groupId'] {
  return track.segments[0]?.groupId ?? ''
}

export function createIvTrack<T extends IvTrackType>(type: T): _IvTrack<T> {
  const base = createTrackItem()

  return {
    ...base,
    type,
    segments: [],
  }
}

export function insertAfterIvTrack(
  track: IvTrack,
  target: IvTrack,
  tracks: IvTrack[],
): IvTrackIndex {
  return insertAfterTrack(track, target, tracks)
}

export function insertBeforeIvTrack(
  track: IvTrack,
  target: IvTrack,
  tracks: IvTrack[],
): IvTrackIndex {
  return insertBeforeTrack(track, target, tracks)
}

export function addIvSegment<T extends IvTrackType>(
  target: InferIvSegment<IvSegment, { type: IvTrackSegmentMapType[T] }>,
  track: _IvTrack<T>,
): void {
  target.groupId = getGroupIdByTrack(track)
  return addTrackSegment(target, track)
}

export function removeIvSegment(
  target: IvSegment,
  tracks: IvTrack[],
  options?: {
    removeTrackIfEmpty?: boolean
  },
): void {
  target.groupId = ''

  // 清除轨道的信息
  return removeTrackSegment(target, tracks, options)
}

type SegmentTypeToTrackType<T extends IvSegmentType> = typeof typeMap[T]
export function getNoConflictTrack<T extends IvSegment>(
  segment: T,
  tracks: IvTrack[],
): _IvTrack<SegmentTypeToTrackType<T['type']>> | undefined {
  const trackType = typeMap[segment.type]

  return tracks.find((track) => {
    if (trackType === track.type) {
      const conflictSegment = getConflictSegment(segment, track)
      return !conflictSegment
    }

    return false
  }) as any
}

export function addIvSegmentToTrack(
  target: IvSegment,
  tracks: IvTrack[],
): boolean {
  if (isSegmentVideo(target) || tracks.length === 0) {
    return false
  }

  if (isSegmentIv(target)) {
    let ivTrack = tracks.find((t) => t.type === IvTrackType.Iv)

    if (!ivTrack) {
      ivTrack = createIvTrack(IvTrackType.Iv)

      tracks.unshift(ivTrack)
    }
    const track = getNoConflictTrack(target, [ivTrack])

    if (track) {
      addIvSegment(target, track)
      return true
    }

    return false
  } else if (isSegmentAudio(target)) {
    return insertToAudioTrack(target, tracks)
  }

  return insertToOtherTrack(target, tracks, { pinToTop: true })
}

export function insertToAudioTrack(
  segment: IvSegmentAudio,
  tracks: IvTrack[],
): boolean {
  const targetTrack = getNoConflictTrack(segment, [...tracks])

  if (targetTrack) {
    addIvSegment(segment, targetTrack)
  } else {
    const track = createIvTrack(IvTrackType.Audio)
    addIvSegment(segment, track)

    const last = tracks[tracks.length - 1]
    if (last) {
      insertAfterIvTrack(track, last, tracks)
    } else {
      return false
    }
  }

  return true
}

export function insertToOtherTrack(
  segment: IvSegmentOther,
  tracks: IvTrack[],
  options?: {
    pinToTop?: boolean
  },
): boolean {
  const trackType = typeMap[segment.type]
  const targetTrack = getNoConflictTrack(segment, tracks)

  if (targetTrack) {
    addIvSegment(segment, targetTrack)
  } else {
    const otherTracks = tracks.filter((t) =>
      [IvTrackType.Hand, IvTrackType.Image, IvTrackType.Text].includes(t.type),
    )
    let relatiedTrack
    // 分两种情况，一种是一定要置顶的，比如新增的节点；另一种是同类型轨道的最上面
    if (options?.pinToTop) {
      relatiedTrack =
        otherTracks[0] ?? tracks.find((t) => t.type === IvTrackType.Video)
    } else {
      relatiedTrack =
        [...otherTracks].find((t) => t.type === trackType) ??
        otherTracks[0] ??
        tracks.find((t) => t.type === IvTrackType.Video)
    }

    const track = createIvTrack(trackType)
    addIvSegment(segment, track)

    if (relatiedTrack) {
      insertBeforeIvTrack(track as any, relatiedTrack, tracks)
      return true
    }

    return false
  }

  return true
}

/**
 * 尝试插入指定轨道，若冲突则向上新建轨道后插入
 */
export function tryInsertIfConflictOrNot<T extends IvTrackType>(
  target: InferIvSegment<IvSegmentOther, { type: IvTrackSegmentMapType[T] }>,
  tracks: IvTrack[],
  options?: {
    targetTrack?: _IvTrack<T>
  },
): boolean {
  const { targetTrack } = options ?? {}
  const _currentTrack = tracks.find((t) => t.id === target.trackId) as
    | _IvTrack<T>
    | undefined
  const _targetTrack = targetTrack ?? _currentTrack

  if (_currentTrack) {
    // 在当前自身轨道，合适！
    if (
      _currentTrack.id === _targetTrack?.id &&
      !getConflictSegment(target, _currentTrack)
    ) {
      return true
    }
  }

  // 没有指定目标轨道，就默认行为
  if (!_targetTrack) {
    removeIvSegment(target, tracks)
    return insertToOtherTrack(target, tracks)
  }

  const matchedTrackType = typeMap[target.type]
  if (matchedTrackType !== _targetTrack.type) {
    console.error(
      `tryInsertIfConflictOrNot error: expected track type to be ${matchedTrackType}, result was ${_targetTrack.type}`,
    )

    return false
  }

  removeIvSegment(target, tracks)

  const conflictSegment = getConflictSegment(target, _targetTrack)
  if (conflictSegment) {
    const newTrack = createIvTrack(_targetTrack.type)
    addIvSegment(target, newTrack)

    return (
      insertBeforeIvTrack(newTrack as any, _targetTrack as any, tracks) > -1
    )
  }

  addIvSegment(target, _targetTrack)
  return true
}

export function patchTracks(data: IvTrack[]): void {
  data.forEach((t) => {
    t.segments.forEach((s) => {
      patchSegment(s, data)
    })

    t.icons = [
      {
        icon: trackIconMap[t.type],
      },
    ]

    t.height = trackHeightMap[t.type]
  })
}
